import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash/fp';
import {
  Message,
  Button,
  ButtonToolbar,
  ControlLabel,
  Form,
  FormGroup,
  FormControl,
  Icon,
  Schema,
  HelpBlock
} from 'rsuite';
import DescriptionEditor from 'react-email-editor';

import {
  createPantry,
  updatePantry
} from '../../../../redux/Settings/sagas/routines';
import { getIsSaving } from '../../../../redux/Settings/selectors/portal/pantry';
import { isJsonString } from '../../../../helpers/utils';
import { PortalUrl } from '../../../../helpers/config';
import * as PERMISSIONS from '../../../../constants/permissions/settings';
import { hasModifyAccess } from '../../../../helpers/permission';
import { InlineFormGroup } from '../../../widgets/FormElements';
import { getUserPermissionsAsMap } from '../../../../redux/selectors/permission';

const { StringType, NumberType } = Schema.Types;

const toPathFormat = value =>
  value
    .trim()
    .replace(/ /g, '-')
    .toLowerCase();

const FormModel = Schema.Model({
  id: NumberType(),
  name: StringType()
    .pattern(
      /^[a-zA-Z0-9- ]+$/,
      'Must only contain letters, numbers, dash (-) and space'
    )
    .isRequired('This field is required'),
  path: StringType()
    .pattern(
      /^[a-z0-9-]+$/,
      'Must only contain lower case letters, numbers and dash (-)'
    )
    .isRequired('This field is required'),
  streetAddress: StringType().isRequired('This field is required'),
  city: StringType()
    .pattern(/^[a-zA-Z- ]+$/, 'Must only contain letters, dash(-) and space')
    .isRequired('This field is required'),
  county: StringType().pattern(
    /^[a-zA-Z-., ]+$/,
    'Must only contain letters, period (.), comma (,), dash (-) and space'
  ),
  state: StringType()
    .pattern(/^[a-zA-Z]+$/, 'Must only contains letters.')
    .minLength(2, 'Must be a valid two (2) characters code')
    .maxLength(2, 'Must be a valid two (2) characters code')
    .isRequired('This field is required'),
  zipCode: StringType()
    .pattern(
      /^[a-zA-Z0-9 ]{1,9}$/,
      'Only accepts numbers, letters and spaces - maximum of 9 characters'
    )
    .isRequired('Required field'),
  contactName: StringType()
    .pattern(
      /^[a-zA-Z0-9- ]+$/,
      'Must only contain letters, numbers, dash (-) and space'
    )
    .isRequired('This field is required'),
  contactEmail: StringType()
    .isRequired('This field is required')
    .isEmail('Invalid Email'),
  contactNumber: StringType()
    .isRequired('This field is required')
    .maxLength(20, 'Maximum of 20 characters')
});

const DefaultValues = {
  id: null,
  name: '',
  path: '',
  description: '',
  streetAddress: '',
  city: '',
  county: '',
  state: '',
  zipCode: '',
  contactName: '',
  contactEmail: '',
  contactNumber: ''
};

const EditorOptions = {
  appearance: { theme: 'dark' },
  tools: {
    image: { enabled: false },
    button: { enabled: false },
    html: { enabled: false },
    menu: { enabled: false }
  }
};

const PantryForm = ({ data }) => {
  const dispatch = useDispatch();
  const formRef = useRef();
  const descriptionRef = useRef(null);
  const [formValue, setFormValue] = useState(DefaultValues);
  const isSaving = useSelector(getIsSaving);
  const permissions = useSelector(getUserPermissionsAsMap);
  const portalUrl = `${PortalUrl}/pantry/${formValue.path || '{path}'}`;
  const [showDescriptionError, setShowDescriptionError] = useState(false);
  const [descriptionCount, setDescriptionCount] = useState(0);
  const allowEdit = hasModifyAccess(PERMISSIONS.PORTAL, permissions);

  const onSave = ({ id = null, body }) => {
    if (id) dispatch(updatePantry({ id, body }));
    else dispatch(createPantry(body));
  };

  const handleFormChange = value => {
    setFormValue(value);
  };

  const handleSubmit = () => {
    if (formRef.current.check()) {
      const { id, ...body } = formValue;

      descriptionRef.current.editor.exportHtml(({ design, chunks }) => {
        const descriptionHtml = JSON.stringify(chunks);
        const description = JSON.stringify(design);

        if (descriptionHtml.length > 100000) {
          setShowDescriptionError(true);
          setDescriptionCount(descriptionHtml.length.toLocaleString());
        } else {
          setShowDescriptionError(false);
          setDescriptionCount(descriptionHtml.length.toLocaleString());
          setFormValue({ ...formValue, description, descriptionHtml });
          onSave({ id, body: { ...body, description, descriptionHtml } });
        }
      });
    }
  };

  const handlePathFocus = () => {
    const { name, path } = formValue;
    if (!path) {
      setFormValue({ ...formValue, path: toPathFormat(name) });
    }
  };

  const handleLoadDescription = () => {
    const { description, descriptionHtml } = formValue;
    if (isJsonString(description)) {
      setDescriptionCount(descriptionHtml.length.toLocaleString());
      descriptionRef.current.editor.loadDesign(JSON.parse(description));
    }
  };

  useEffect(
    () => {
      if (!isEmpty(data)) setFormValue(data);
    },
    [data]
  );

  return (
    <Form
      ref={formRef}
      formValue={formValue}
      model={FormModel}
      onChange={handleFormChange}
    >
      <FormGroup>
        {!allowEdit && (
          <Message
            type="info"
            description="You only have VIEW permission for Pantry Details. Editing form is restricted."
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <InlineFormGroup>
          <ControlLabel>Name</ControlLabel>
          <FormControl name="name" readOnly={!allowEdit} />
        </InlineFormGroup>
        <InlineFormGroup>
          <ControlLabel>Path</ControlLabel>
          <FormControl
            name="path"
            onFocus={handlePathFocus}
            readOnly={!allowEdit}
          />
          <HelpBlock>
            {'Portal URL: '}
            {formValue.id ? (
              <a href={portalUrl} target="_blank" rel="noopener noreferrer">
                {portalUrl}
              </a>
            ) : (
              portalUrl
            )}
          </HelpBlock>
        </InlineFormGroup>
      </FormGroup>
      <FormGroup>
        <InlineFormGroup>
          <ControlLabel>Contact Name *</ControlLabel>
          <FormControl name="contactName" readOnly={!allowEdit} />
        </InlineFormGroup>
        <InlineFormGroup>
          <ControlLabel>Contact Email *</ControlLabel>
          <FormControl name="contactEmail" readOnly={!allowEdit} />
        </InlineFormGroup>
      </FormGroup>
      <FormGroup>
        <InlineFormGroup>
          <ControlLabel>Contact Number *</ControlLabel>
          <FormControl name="contactNumber" readOnly={!allowEdit} />
        </InlineFormGroup>
        <InlineFormGroup>
          <ControlLabel>Street Address *</ControlLabel>
          <FormControl name="streetAddress" readOnly={!allowEdit} />
        </InlineFormGroup>
      </FormGroup>
      <FormGroup>
        <InlineFormGroup>
          <ControlLabel>City/Town *</ControlLabel>
          <FormControl name="city" readOnly={!allowEdit} />
        </InlineFormGroup>
        <InlineFormGroup>
          <ControlLabel>County</ControlLabel>
          <FormControl name="county" readOnly={!allowEdit} />
        </InlineFormGroup>
      </FormGroup>
      <FormGroup>
        <InlineFormGroup>
          <ControlLabel>State/Province *</ControlLabel>
          <FormControl name="state" readOnly={!allowEdit} />
        </InlineFormGroup>
        <InlineFormGroup>
          <ControlLabel>Zip/Postal Code *</ControlLabel>
          <FormControl name="zipCode" readOnly={!allowEdit} />
        </InlineFormGroup>
      </FormGroup>
      <FormGroup>
        <InlineFormGroup>
          <ControlLabel>
            Description <i>({descriptionCount} of 100,000)</i>
          </ControlLabel>
          {showDescriptionError && (
            <span
              style={{
                color: '#f44336',
                border: '1px solid #e5e5ea',
                padding: '4px 8px',
                borderRadius: '6px'
              }}
            >
              Maximum characters of 100,000 combination of Text, styles and html
              Tags. Current characters count: {descriptionCount}
            </span>
          )}
        </InlineFormGroup>
        <DescriptionEditor
          ref={descriptionRef}
          onLoad={handleLoadDescription}
          options={EditorOptions}
        />
      </FormGroup>

      <ButtonToolbar style={{ textAlign: 'right' }}>
        <Button
          appearance="primary"
          onClick={handleSubmit}
          disabled={isSaving || !allowEdit}
        >
          <Icon icon="save" /> {isSaving ? 'Saving...' : 'Save'}
        </Button>
      </ButtonToolbar>
    </Form>
  );
};

PantryForm.propTypes = {
  data: PropTypes.shape({})
};

PantryForm.defaultProps = {
  data: {}
};

export default PantryForm;
